import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, Check, Music, Image, Zap, ArrowLeft, Loader2, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const planDetails = {
  essentials: {
    name: "Essentials Plan",
    icon: Music,
    price: 35,
    features: [
      "One personalized song created from your story",
      "Custom lyrics and music",
      
      "Digital MP3 delivery",
      "Languages: English, Spanish, or bilingual lyrics",
    ],
  },
  premium: {
    name: "Premium Song + Slideshow",
    icon: Image,
    price: 85,
    features: [
      "One personalized song",
      
      "Slideshow video with 15–40 photos",
      "Professionally paced visuals",
      "Digital MP4 video delivery",
      "Digital lyric sheet",
      "Everything in Essentials, plus:",
    ],
  },
};


const Checkout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [rushDelivery, setRushDelivery] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState("");

  const planParam = searchParams.get("plan") as "essentials" | "premium" | null;
  const plan = planParam && planDetails[planParam] ? planDetails[planParam] : null;

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      const returnUrl = `/checkout?plan=${planParam}`;
      sessionStorage.setItem("authReturnUrl", returnUrl);
      navigate("/login");
    }
  }, [user, loading, navigate, planParam]);

  // Redirect if no valid plan selected
  useEffect(() => {
    if (!plan && !loading) {
      navigate("/pricing");
    }
  }, [plan, loading, navigate]);

  if (loading || !user || !plan) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const IconComponent = plan.icon;
  const rushPrice = 10;
  const totalPrice = plan.price + (rushDelivery ? rushPrice : 0);

  const handleCheckout = async () => {
    setIsProcessing(true);
    try {
          const trimmedCode = couponCode.trim().toUpperCase();
          const { data, error } = await supabase.functions.invoke("create-checkout", {
            body: {
              planType: planParam,
              rushDelivery,
              couponCode: trimmedCode || undefined,
              successUrl: `${window.location.origin}/checkout/success?plan=${planParam}&rush=${rushDelivery}&session_id={CHECKOUT_SESSION_ID}`,
              cancelUrl: `${window.location.origin}/checkout?plan=${planParam}`,
            },
          });

      if (error) throw error;
      if (data?.error) {
        toast.error(data.error);
        setIsProcessing(false);
        return;
      }
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      console.error("Checkout error:", err);
      toast.error("Payment failed. Please try again.");
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Back Button */}
          <Button
            variant="ghost"
            className="mb-6"
            onClick={() => navigate("/pricing")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Pricing
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Order Summary */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-card rounded-2xl p-6 md:p-8 shadow-soft h-fit"
            >
              <h2 className="font-heading text-2xl font-bold text-foreground mb-6">
                Order Summary
              </h2>

              <div className="flex items-start gap-4 mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full shrink-0">
                  <IconComponent className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    {plan.name}
                  </h3>
                  <p className="text-2xl font-bold text-foreground mt-1">
                    ${plan.price}
                  </p>
                </div>
              </div>

              <ul className="space-y-2 mb-6">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="border-t border-border pt-4">
                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-accent/10 transition-colors">
                  <Checkbox
                    checked={rushDelivery}
                    onCheckedChange={(checked) => setRushDelivery(checked as boolean)}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-accent-foreground" />
                      <span className="font-medium text-foreground">Rush Delivery</span>
                      <span className="text-foreground font-bold">+${rushPrice}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Guaranteed delivery within 24 hours
                    </p>
                  </div>
                </label>
              </div>

              <div className="border-t border-border mt-4 pt-4">
                <div className="flex justify-between items-center text-lg font-bold">
                  <span className="text-foreground">Total</span>
                  <span className="text-foreground">${totalPrice}</span>
                </div>
              </div>
            </motion.div>

            {/* Payment Section */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-card rounded-2xl p-6 md:p-8 shadow-soft"
            >
              <h2 className="font-heading text-2xl font-bold text-foreground mb-6">
                Payment
              </h2>

              <div className="bg-muted/50 border border-border rounded-xl p-6 mb-6">
                <div className="text-center text-muted-foreground">
                  <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">
                    You'll be redirected to Stripe's secure checkout to complete payment.
                  </p>
                </div>
              </div>

              {/* Coupon Code */}
              <div className="mb-6">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Coupon Code
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter coupon code"
                      className="pl-9"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Have a promo code? Enter it above — it will be applied at checkout.
                </p>
              </div>

              <Button
                variant="hero"
                size="lg"
                className="w-full"
                onClick={handleCheckout}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Complete Purchase — ${totalPrice}
                  </>
                )}
              </Button>

              <p className="text-xs text-muted-foreground text-center mt-4">
                By completing this purchase, you agree to our terms of service.
                Your payment is secure and encrypted.
              </p>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Checkout;
